import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useGlobalStore } from '@/store/global-store-base';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import { createSharedRuns } from '@lib/shared-runs';
import type { TableViewDto } from '@/ApiClient';

/**
 * Saved column setups, owned by the current user. Loaded for every table at once: a table's
 * default view decides what "reset columns" restores, whether or not it shows the views bar.
 */
export const useTableViews = defineStore('table-views', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.tableView_List,
    dto: 'TableViewDto',
    create: api.tableView_Create,
    update: api.tableView_Update,
    delete: api.tableView_Delete,
    signalR: 'TableViewUpdate',
  });

  const byTableKey = computed(
    () => (tableKey: string) =>
      globalStoreBase.loaded.value.filter((view) => view.tableKey === tableKey).sort((a, b) => a.order - b.order)
  );

  const defaultView = computed(
    () => (tableKey: string) =>
      globalStoreBase.loaded.value.find((view) => view.tableKey === tableKey && view.isDefault)
  );

  async function fork(viewId: number): Promise<TableViewDto> {
    const view = await api.tableView_Fork(useUser().brandId, viewId);
    await globalStoreBase.loadAll();

    return view;
  }

  // Both endpoints return the full list, so there is nothing to refetch afterwards.
  async function setDefault(viewId: number) {
    globalStoreBase.replaceAll(await api.tableView_SetDefault(useUser().brandId, viewId));
  }

  async function reorder(tableKey: string, viewIds: number[]) {
    globalStoreBase.replaceAll(await api.tableView_Reorder(useUser().brandId, { tableKey, viewIds }));
  }

  /** Resolves a shared link, which may point at a view owned by a colleague. */
  const getShared = (viewId: number) => api.tableView_GetShared(useUser().brandId, viewId);

  /**
   * Runs a table's first-visit seeding once, however many views bars ask for it. One table can be
   * open in two places at once — the products overview and the product page's article table share
   * a tableKey — and each bar seeds on mount, before any of them has recorded that the table was
   * seeded. Without this they each save the whole preset list, leaving the user two of every
   * starter view.
   */
  const seedOnce = createSharedRuns<TableViewDto | null>();

  return { ...globalStoreBase, byTableKey, defaultView, fork, setDefault, reorder, getShared, seedOnce };
});
